import {useEffect, useState} from "react"

import {ChatbotEvent, SponsoredContent} from "@proto/chatbot/v1alpha/chatbot"
import {BOTNAME, BotSegment} from "@/constants"
import {useWebsocket} from "@/hooks/useWebsocket"
import {
    EventTypeArticleHitsReport,
    EventTypeHeader,
    EventTypeMessage,
    EventTypeProcessingContent,
    EventTypeProcessingQuery,
    EventTypeProcessingResponse,
    EventTypeProduct,
    EventTypeProducts,
    EventTypeRelated,
    EventTypeReportThresholds,
    EventTypeSegmentDistance,
    EventTypeSegmentInfo,
    EventTypeSelect,
    EventTypeSelectWithText,
    EventTypeSimilarSegment,
    EventTypeSponsoredAdverts,
    EventTypeSponsoredContent,
    EventTypeSponsoredSignOff,
    EventTypeStart,
    EventTypeStream,
    EventTypeTable,
    EventTypeUser,
} from "@/lib/chat/messages"
import ArticleHitsReportMessage from "./ArticleHitsReportMessage"
import BotMessage from "./BotMessage"
import HeaderMessage from "./HeaderMessage"
import ProductMessage from "./ProductMessage"
import ProductsMessage from "./ProductsMessage"
import RelatedMessage from "./RelatedMessage"
import ReportThresholdsMessage from "./ReportThresholdsMessage"
import SegmentDistanceMessage from "./SegmentDistanceMessage"
import {SegmentInfo, SegmentInfoPanel} from "./SegmentInfo"
import SelectMessage from "./SelectMessage"
import SimilarSegmentTable from "./SimilarSegmentTable"
import SponsoredAdvertsMessage from "./SponsoredAdvertsMessage"
import SponsoredContentMessage from "./SponsoredContentMessage"
import SponsoredSignOffMessage from "./SponsoredSignOffMessage"
import TableMessage from "./TableMessage"
import UserMessage from "./UserMessage"

interface IProps {
    setUserInputEnabled: React.Dispatch<React.SetStateAction<boolean>>
}

const newSessionMessage: ChatbotEvent = {
    type: "message",
    message: "I'm sorry, I lost track of where we were, forgive me for restarting the conversation...",
    id: "",
    clientId: "",
}

const ChatPanel: React.FC<IProps> = ({setUserInputEnabled}) => {
    const {messages, responseHasTimedOut, closeWebsocket, websocket} = useWebsocket()
    const [segmentInfo, setSegmentInfo] = useState<SegmentInfo | null>(null)

    useEffect(() => {
        if (messages.length === 0) {
            return
        }

        let lastMessage = messages[messages.length - 1]
        switch (lastMessage.type) {
            case EventTypeSelect:
                try {
                    const sel = JSON.parse(lastMessage.message)
                    if (sel.id === "intro_prompts" || sel.id === "other_budget") {
                        setUserInputEnabled(true)
                        return
                    }
                    setUserInputEnabled(false)
                    return
                } catch (e) {
                    return
                }
            case EventTypeReportThresholds:
                setUserInputEnabled(false)
                return
            case EventTypeSegmentDistance:
                setUserInputEnabled(false)
                return
            case EventTypeStream:
            case EventTypeUser:
            case EventTypeHeader:
            case EventTypeProcessingQuery:
            case EventTypeProcessingContent:
            case EventTypeProcessingResponse:
                setUserInputEnabled(false)
                break
            default:
                setUserInputEnabled(true)
                break
        }
    }, [messages])

    useEffect(() => {
        if (responseHasTimedOut && websocket) {
            // Close the socket, so it will automatically reconnect
            closeWebsocket(websocket)
        }
    }, [responseHasTimedOut])

    const [processedMessages, setProcessedMessages] = useState<ChatbotEvent[]>([])

    // Process messages when they change
    useEffect(() => {
        let tempMessages: ChatbotEvent[] = []

        messages.forEach((message, index) => {
            if (index > 1 && message.type === EventTypeStart) {
                tempMessages.push(newSessionMessage)
            }

            if (message.message === undefined) {
                return
            }

            // Only handle SegmentInfo messages if this is the segment bot
            if (message.type === EventTypeSegmentInfo && BOTNAME === BotSegment) {
                try {
                    const info = JSON.parse(message.message) as SegmentInfo
                    setSegmentInfo(info)
                    return
                } catch (e) {
                    console.error("Failed to parse SegmentInfo:", e)
                    return
                }
            }

            if (message.type === "stream" && index > 0) {
                if (tempMessages.length == 0 || tempMessages[tempMessages.length - 1].type !== EventTypeMessage) {
                    let serverMessage: ChatbotEvent = {
                        type: EventTypeMessage,
                        message: message.message,
                        id: message.id,
                        clientId: message.clientId,
                    }
                    tempMessages.push(serverMessage)
                } else {
                    tempMessages[tempMessages.length - 1].message += message.message
                }
            } else {
                tempMessages.push(message)
            }
        })

        setProcessedMessages(tempMessages)
    }, [messages])

    return (
        <>
            {BOTNAME === BotSegment && <SegmentInfoPanel segmentInfo={segmentInfo} />}
            {processedMessages.map((msg, i) => {
                switch (msg.type) {
                    case EventTypeUser:
                        return <UserMessage key={i} message={msg?.message} />
                    case EventTypeRelated:
                        return <RelatedMessage key={i} message={msg?.message} />
                    case EventTypeSelect:
                        return <SelectMessage key={i} message={msg?.message} />
                    case EventTypeSelectWithText:
                        return <SelectMessage key={i} message={msg?.message} />
                    case EventTypeProducts:
                        return <ProductsMessage key={i} message={msg?.message} />
                    case EventTypeProduct:
                        return <ProductMessage key={i} message={msg?.message} />
                    case EventTypeHeader:
                        return <HeaderMessage key={i} message={msg?.message} />
                    case EventTypeMessage:
                        return <BotMessage key={`bm_${i}`} message={msg?.message} />
                    case EventTypeSponsoredContent:
                        return <SponsoredContentMessage key={i} message={msg?.message} />
                    case EventTypeSponsoredAdverts:
                        return <SponsoredAdvertsMessage key={i} message={msg?.message} />
                    case EventTypeSponsoredSignOff:
                        return <SponsoredSignOffMessage key={i} message={msg?.message} />
                    case EventTypeArticleHitsReport:
                        return <ArticleHitsReportMessage key={i} message={msg?.message} />
                    case EventTypeReportThresholds:
                        return <ReportThresholdsMessage key={i} message={msg?.message} />
                    case EventTypeSegmentDistance:
                        return <SegmentDistanceMessage key={i} message={msg?.message} />
                    case EventTypeTable:
                        return <TableMessage key={i} message={msg?.message} />
                    case EventTypeSimilarSegment:
                        return <SimilarSegmentTable key={i} message={msg?.message} />
                    default:
                        return null
                }
            })}
        </>
    )
}

export default ChatPanel
